/**
 * «¿Para qué consorcio?», adentro del formulario de la venta.
 *
 * QUE PROBLEMA RESUELVE
 * ---------------------
 * El dueño lo pidio con estas palabras: «a la hora de la venta tenes que
 * seleccionar el cliente que es el administrador y tenes que tener la opcion de
 * poner el consorcio, para poder ver ventas unicamente de consorcios,
 * administradores, y eso suma todas las ventas de una administracion».
 *
 * Hasta hoy el consorcio se ponia DESPUES, con una accion sobre la venta ya
 * guardada. Funcionaba y nadie la usaba: quien carga una venta la carga una vez,
 * y un segundo paso que hay que acordarse de dar es un dato que no se carga. Sin
 * ese dato, las tres vistas que el dueño necesita —por consorcio, por
 * administrador, por administracion— quedan vacias.
 *
 * POR QUE UN WIDGET PROPIO Y NO EL `many2one` GENERICO
 * ----------------------------------------------------
 * Porque `destino_id` NO es una ForeignKey: es una columna entera y opaca de
 * `sales`, que no puede apuntar a `property.building` sin invertir el grafo de
 * modulos (ARQUITECTURA.md §8). El selector generico se dibuja a partir de
 * `meta.relation`, que acá no existe y no puede existir.
 *
 * Entonces el reparto es el de siempre: la columna es del modulo de abajo, el
 * SIGNIFICADO lo pone el vertical —que declara este widget en su extensor del
 * spec— y este componente traduce entre las dos formas: hacia afuera muestra un
 * edificio con su nombre, hacia el servidor escribe un numero.
 *
 * Y EL VINCULO DE VERDAD LO ESCRIBE EL SERVIDOR
 * ---------------------------------------------
 * Este campo guarda un numero. El hecho —el que valida que ese cliente pueda
 * comprar para ese edificio— es `property.VentaDeConsorcio`, y lo mantiene al
 * dia el `post_save` de la venta (`sincronizar_desde_el_selector`). Acá no se
 * escribe ninguna regla de negocio: si se eligiera un edificio de otra cartera,
 * quien se niega es el servidor, en castellano.
 */

import { useEffect, useState } from 'react';

import { ReadonlyOutput } from '@/components/fields/FieldDisplay';
import { RelationPicker } from '@/components/fields/RelationPicker';
import type { FieldInputProps } from '@/components/fields/types';
import { api } from '@/lib/api';
import type { Many2OneValue, RecordData } from '@/lib/types';

/** El modelo del que se eligen los edificios. Lo sabe el vertical, no `sales`. */
const MODELO = 'property.building';

export function ConsorcioDeLaVentaField({
  id,
  meta,
  value,
  onChange,
  readonly,
  required,
  invalid,
  describedBy,
}: FieldInputProps) {
  const idElegido =
    typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;

  // EL NOMBRE SE BUSCA, no viaja con el valor. La columna guarda un numero
  // pelado, asi que al abrir una venta ya cargada lo unico que hay es el id: sin
  // esto el campo mostraria «900003», que no le dice nada a nadie.
  const [nombre, setNombre] = useState<string>('');
  const [editando, setEditando] = useState(false);

  useEffect(() => {
    if (idElegido === null) {
      setNombre('');
      return;
    }
    let vigente = true;
    void api
      .readRecord(MODELO, idElegido, ['display_name'])
      .then((registro: RecordData) => {
        if (vigente) setNombre(String(registro.display_name ?? ''));
      })
      .catch(() => {
        // El edificio se archivo o se borro: se muestra el numero antes que
        // mentir con un nombre viejo, y el usuario puede elegir otro.
        if (vigente) setNombre(`#${idElegido}`);
      });
    return () => {
      vigente = false;
    };
  }, [idElegido]);

  if (readonly) {
    return (
      <ReadonlyOutput
        id={id}
        meta={{ ...meta, type: 'char' }}
        value={nombre}
        {...(describedBy === undefined ? {} : { describedBy })}
      />
    );
  }

  if (idElegido !== null && !editando) {
    return (
      <div className="flex items-stretch gap-1.5">
        <button
          id={id}
          type="button"
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          onClick={() => {
            setEditando(true);
          }}
          className="touch-target flex min-w-0 flex-1 items-center rounded-md border border-line bg-surface px-3 py-2 text-left text-sm transition-colors hover:border-line-strong sm:min-h-0 sm:h-9 sm:py-1.5"
        >
          <span className="truncate">{nombre === '' ? '…' : nombre}</span>
        </button>
        {/* VACIARLO ES UNA OPCION LEGITIMA y por eso el boton esta siempre: un
            tramite que se anoto para un edificio y en realidad era la matricula
            del administrador se corrige sacandole el consorcio, no borrando la
            venta. */}
        <button
          type="button"
          onClick={() => {
            onChange(null);
            setEditando(false);
          }}
          title="Sacarle el consorcio: es un trámite del administrador"
          aria-label="Sacarle el consorcio"
          className="touch-target rounded-md border border-line px-2.5 text-sm text-fg-muted transition-colors hover:border-line-strong hover:text-fg sm:min-h-0 sm:h-9"
        >
          ✕
        </button>
      </div>
    );
  }

  return (
    <RelationPicker
      id={id}
      relation={MODELO}
      required={required}
      invalid={invalid}
      autoFocus={editando}
      placeholder="Buscar consorcio…"
      {...(describedBy === undefined ? {} : { describedBy })}
      onPick={(registro: Many2OneValue) => {
        // HACIA EL SERVIDOR VIAJA EL NUMERO, no el objeto: la columna es un
        // entero y mandarle `{id, display_name}` la haria rebotar.
        onChange(registro.id);
        setNombre(String(registro.display_name ?? ''));
        setEditando(false);
      }}
    />
  );
}
